/**
 * Iter-292g — P0 fix: Community welcome modal trap.
 *
 * Three production failures, one rewrite:
 *
 *   1. The previous version had TWO separate `useState` instances of the
 *      "seen" flag — one in the page (talent/Home, admin/Home, etc.) and
 *      one inside the modal itself. Dismissing in the modal flipped the
 *      modal's state but the page never knew, so the timer kept rearming
 *      the modal on subsequent renders / remounts.
 *
 *   2. The modal rendered as a deep child of `<main>`. iOS Safari + any
 *      ancestor with `transform`, `filter`, `position: sticky`, or
 *      `backdrop-filter` (which we have on the dashboard header) becomes
 *      a stacking + containing context — the modal's `fixed inset-0` no
 *      longer escaped the parent, and the gradient header could paint
 *      over the buttons in some viewports. Fixed by mounting the modal
 *      at `document.body` via a Portal.
 *
 *   3. Any throw inside `dismiss()` left the user trapped. Now every
 *      close path is wrapped, the Escape key always closes, the
 *      backdrop click always closes, and a 60 s safety timer
 *      auto-dismisses if the React state ever wedges.
 *
 * The flag is stored in localStorage AND broadcast across React
 * components via a tiny pub-sub so EVERY hook instance sees the same
 * value. If the page or another modal calls dismiss, every subscriber
 * receives the new state synchronously.
 *
 * No server call — community onboarding state is a UX nicety, never
 * worth a network round-trip. Safari Private Mode users will see the
 * modal once per visit (acceptable).
 */
import React, { useEffect, useState, useCallback, useRef, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Sparkles, Trophy, Users, Camera, X } from 'lucide-react';

const FLAG_KEY = 'community_intro_seen_v1';

// ─────────────────────────────────────────────────────────────────────────
// Shared external store — page + modal both read from here.
// Survives across mount/unmount cycles and propagates `dismiss` calls.
// ─────────────────────────────────────────────────────────────────────────
const _subscribers = new Set();

function _readSeen() {
  try {
    return localStorage.getItem(FLAG_KEY) === '1';
  } catch {
    // Safari Private Mode / SecurityError. Treat as "seen" so the modal
    // never traps the user when storage is unavailable.
    return true;
  }
}

function _writeSeen() {
  try {
    localStorage.setItem(FLAG_KEY, '1');
  } catch {
    // Ignore quota / SecurityError. The in-memory subscription will
    // still propagate the dismissal for this tab so the modal closes
    // and stays closed for the session.
  }
}

let _seenSnapshot = (typeof window !== 'undefined') ? _readSeen() : true;

function _subscribe(cb) {
  _subscribers.add(cb);
  return () => _subscribers.delete(cb);
}

function _getSnapshot() {
  return _seenSnapshot;
}

function _setSeen(v) {
  if (_seenSnapshot === v) return;
  _seenSnapshot = v;
  _subscribers.forEach((cb) => { try { cb(); } catch { /* noop */ } });
}

export function useCommunityIntroFlag() {
  // useSyncExternalStore ensures every consumer (page + modal) sees the
  // SAME value and re-renders together when dismiss() runs anywhere.
  const seen = useSyncExternalStore(_subscribe, _getSnapshot, () => true);
  const dismiss = useCallback(() => {
    _writeSeen();
    _setSeen(true);
  }, []);
  return { seen, dismiss };
}

// ─────────────────────────────────────────────────────────────────────────
// Modal — every close path is fail-safe, portal-mounted, ESC-aware.
// ─────────────────────────────────────────────────────────────────────────
export default function CommunityIntroModal({ open, onClose }) {
  const navigate = useNavigate();
  const { dismiss } = useCommunityIntroFlag();
  const closingRef = useRef(false);

  // ── Bulletproof close — runs every path independently. We catch
  // every possible throw so the user is never trapped, and we always
  // call onClose() last (in a microtask) so React reliably re-renders.
  const close = useCallback(() => {
    if (closingRef.current) return;
    closingRef.current = true;
    try { dismiss(); } catch (e) { /* never throw inside close */ }
    queueMicrotask(() => {
      try { onClose?.(); } catch (e) { /* never throw */ }
      closingRef.current = false;
    });
  }, [dismiss, onClose]);

  const visit = useCallback(() => {
    close();
    // Defer navigation a tick so React paints the modal closing first.
    setTimeout(() => {
      try { navigate('/community'); } catch (e) { /* never throw */ }
    }, 0);
  }, [close, navigate]);

  // ── Escape key always closes, on every browser.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape' || e.key === 'Esc') close(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close]);

  // ── Last-ditch safety net. If something wedges, auto-dismiss after
  // 60 s so the user is never trapped indefinitely.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(close, 60_000);
    return () => clearTimeout(t);
  }, [open, close]);

  if (!open) return null;
  if (typeof document === 'undefined') return null;

  const modal = (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: 2147483000 /* above any in-app overlay incl. Toaster */ }}
      data-testid="community-intro-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="community-intro-title"
    >
      {/* Backdrop — pointer-events on; clicking dismisses. */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={close}
        data-testid="community-intro-backdrop"
      />

      {/* Panel — separate from the backdrop in DOM + has its own stacking. */}
      <div
        className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
        style={{ zIndex: 1 }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={close}
          onTouchEnd={(e) => { e.preventDefault(); close(); }}
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/95 hover:bg-white grid place-items-center text-slate-700 shadow ring-1 ring-slate-200"
          style={{ zIndex: 2 }}
          data-testid="community-intro-close"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-6 bg-gradient-to-br from-fuchsia-500 via-pink-500 to-amber-400 text-white">
          <div className="w-12 h-12 rounded-full bg-white/20 grid place-items-center mb-3">
            <Sparkles className="w-6 h-6" />
          </div>
          <h2 id="community-intro-title" className="text-xl font-bold leading-tight">
            Welcome to the iEvntür Community
          </h2>
          <p className="text-sm opacity-90 mt-1">
            Where the network celebrates, connects, and grows together.
          </p>
        </div>

        <div className="p-6 space-y-3">
          <Feature icon={<Trophy className="w-4 h-4 text-fuchsia-600" />} text="Celebrate achievements" />
          <Feature icon={<Users className="w-4 h-4 text-pink-600" />} text="Connect with creators and talent" />
          <Feature icon={<Sparkles className="w-4 h-4 text-amber-600" />} text="Discover top performers" />
          <Feature icon={<Camera className="w-4 h-4 text-emerald-600" />} text="Share activation photos" />
        </div>

        <div className="p-5 pt-2 flex flex-col sm:flex-row gap-2">
          <Button
            type="button"
            onClick={close}
            onTouchEnd={(e) => { e.preventDefault(); close(); }}
            variant="outline"
            className="flex-1"
            data-testid="community-intro-later"
          >
            Maybe Later
          </Button>
          <Button
            type="button"
            onClick={visit}
            onTouchEnd={(e) => { e.preventDefault(); visit(); }}
            className="flex-1 bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
            data-testid="community-intro-visit"
          >
            Visit Community
          </Button>
        </div>
      </div>
    </div>
  );

  // Mount at document.body so no ancestor with `transform`, `filter`,
  // `position: sticky`, or `backdrop-filter` can create a containing
  // block that breaks `fixed inset-0` or steals pointer events.
  return createPortal(modal, document.body);
}

function Feature({ icon, text }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 rounded-lg bg-slate-50 border border-slate-100 grid place-items-center shrink-0">
        {icon}
      </div>
      <div className="text-sm text-slate-800">{text}</div>
    </div>
  );
}
